import { makeAutoObservable, runInAction } from "mobx";
import { ConversionProps } from "@/components-v2/FilePreview";
import { failure, isFailure, isOk, isPending, ok, pending, Result } from "@/common/common";
import { TMessage } from "@/i18n/tt";
import { getExtension } from "@/utils/files";
import { logError } from "@/utils/log";
import { getFilenameFromHeaders } from "@/api";
import { AxiosInstance } from "axios";
import { UrnReference, urnReferenceToStringValue } from "@/utils/UrnReference";
import { DbotEventDispatcher } from "@/dbot/common/commonDomain";

export const supportedFiletypesForConversion = [
  "docx",
  "doc",
  "txt",
  "odt",
  "csv",

  "xlsx",
  "xls",
  "ods",

  "pptx",
  "ppt",

  "jpg",
  "jpeg",
  "png",
  "tiff",
  "svg",
];

export const isConversionSupported = (filename: string): boolean => {
  const extension = getExtension(filename);
  const isSupported = supportedFiletypesForConversion.includes(extension ?? "");
  if (!isSupported) {
    logError(`File '${extension}' is not supported!`);
  }
  return isSupported;
};

export class ConversionModel implements ConversionProps {
  conversionResult?: Result<string>;
  keepDiscardResult?: Result<{ data: Blob; filename: string; newDocumentId?: string }>;
  isCancelled = false;
  conversionId?: string;

  constructor(
    readonly http: AxiosInstance,
    readonly context: UrnReference,
    private readonly inFilename: string,
    private dispatch: DbotEventDispatcher,
  ) {
    makeAutoObservable(this, { http: false }, { autoBind: true });
  }

  get isSupportedFileType() {
    return isConversionSupported(this.filename);
  }

  get originalFileType() {
    return getExtension(this.inFilename);
  }

  get filename() {
    return isOk(this.keepDiscardResult) ? this.keepDiscardResult.data.filename : this.inFilename;
  }

  *onConvert(autoKeep?: boolean, onSuccess?: () => void, onFailure?: () => void) {
    this.conversionResult = pending();
    this.isCancelled = false;
    if (this.context) {
      try {
        const ids: string[] = yield this.convertFileToPdf(urnReferenceToStringValue(this.context));
        this.conversionId = ids[0];
      } catch (e) {
        this.onConversionFailed(onFailure);
        return;
      }
      if (this.isCancelled) {
        yield this.onCancel();
        return;
      }
      this.checkStatusInBackground(autoKeep, onSuccess, undefined, onFailure);
    } else {
      logError("Context is missing in onConvert");
      this.onConversionFailed(onFailure);
    }
  }

  checkStatusInBackground(
    autoKeep?: boolean,
    onSuccess?: () => void,
    documentConversionId?: string,
    onFailure?: () => void,
  ) {
    if (documentConversionId) {
      this.conversionId = documentConversionId;
    }
    if (!this.conversionId) {
      this.onConversionFailed(onFailure);
      return;
    }
    this.conversionResult = pending();
    this.dispatch({
      type: "dbot/update-uploading-attachment-props",
      reference: this.context,
      filename: this.inFilename,
      documentConversionId: this.conversionId,
      isConverting: true,
      originalFileType: this.originalFileType,
    });
    const checkStatus = () => {
      if (this.isCancelled) {
        clearInterval(intervalId);
        return;
      }
      if (!this.conversionId) {
        return;
      }
      this.checkConversionStatus(this.conversionId)
        .then((result) => {
          runInAction(() => {
            if (this.isCancelled || result.status === "CANCELLED") {
              this.isCancelled = true;
              this.conversionResult = undefined;
              clearInterval(intervalId);
            } else if (result.status === "COMPLETED") {
              this.conversionResult = ok(result.id);
              clearInterval(intervalId);
              if (autoKeep) {
                this.onKeep(onSuccess, onFailure);
              } else {
                this.dispatch({
                  type: "dbot/update-uploading-attachment-props",
                  reference: this.context,
                  filename: this.inFilename,
                  documentConversionId: this.conversionId,
                  isConverting: false,
                  originalFileType: undefined,
                });
              }
            } else if (result.status === "FAILED") {
              clearInterval(intervalId);
              this.onConversionFailed(onFailure);
            }
          });
        })
        .catch(() => {
          clearInterval(intervalId);
          if (!this.isCancelled) {
            this.onConversionFailed(onFailure);
          }
        });
    };
    const intervalId = setInterval(checkStatus, 500);
  }

  *onCancel() {
    if (isPending(this.keepDiscardResult)) {
      return;
    }
    this.isCancelled = true;
    const conversionId = this.conversionId;
    if (!conversionId) {
      return;
    }
    this.conversionId = undefined;
    this.conversionResult = undefined;
    this.keepDiscardResult = undefined;
    this.dispatch({
      type: "dbot/update-uploading-attachment-props",
      reference: this.context,
      filename: this.inFilename,
      documentConversionId: undefined,
      isConverting: false,
      originalFileType: undefined,
    });
    try {
      yield this.cancelConversion(conversionId);
    } catch (e) {
      yield this.discardQuietly(conversionId);
    }
  }

  *onKeep(onSuccess?: () => void, onFailure?: () => void) {
    if (!isOk(this.conversionResult)) {
      return;
    }
    try {
      this.keepDiscardResult = pending();
      const { data, filename, newDocumentId } = yield this.keepDocumentConversion(
        this.conversionResult.data,
      );
      this.dispatch([
        {
          type: "dbot/update-uploading-attachment-props",
          reference: this.context,
          filename,
          documentConversionId: undefined,
          isConverting: false,
          originalFileType: this.originalFileType,
        },
      ]);
      this.keepDiscardResult = ok({ data, filename, newDocumentId });
      this.conversionId = undefined;
      this.conversionResult = undefined;
      if (typeof onSuccess === "function") {
        onSuccess();
      }
    } catch (e) {
      logError("e", e);
      this.keepDiscardResult = failure();
      this.dispatch({
        type: "dbot/update-uploading-attachment-props",
        reference: this.context,
        filename: this.inFilename,
        documentConversionId: this.conversionId,
        isConverting: false,
        originalFileType: undefined,
      });
      if (typeof onFailure === "function") {
        onFailure();
      }
    }
  }

  *onConversionFailed(onFailure?: () => void) {
    const conversionId = this.conversionId;
    this.conversionResult = failure();
    this.conversionId = undefined;
    this.dispatch({
      type: "dbot/update-uploading-attachment-props",
      reference: this.context,
      filename: this.inFilename,
      documentConversionId: undefined,
      isConverting: false,
      originalFileType: undefined,
    });
    if (typeof onFailure === "function") {
      onFailure();
    }
    if (!conversionId) {
      return;
    }
    yield this.discardQuietly(conversionId);
  }

  *discardQuietly(conversionId: string) {
    try {
      yield this.discardDocumentConversion(conversionId);
    } catch (e) {
      logError("e", e);
    }
  }

  *onBackToOriginalFile() {
    if (!isOk(this.conversionResult)) {
      return;
    }
    this.keepDiscardResult = pending();
    try {
      yield this.discardDocumentConversion(this.conversionResult.data);
      this.conversionId = undefined;
      this.keepDiscardResult = undefined;
      this.conversionResult = undefined;
      this.dispatch({
        type: "dbot/update-uploading-attachment-props",
        reference: this.context,
        filename: this.inFilename,
        documentConversionId: undefined,
        isConverting: false,
        originalFileType: undefined,
      });
    } catch (e) {
      this.keepDiscardResult = failure();
    }
  }

  get keepDiscardResultMessage(): Result<TMessage, TMessage> | undefined {
    if (isOk(this.keepDiscardResult)) {
      return ok({
        key: "file-preview.convert-success",
        params: { extension: getExtension(this.inFilename) },
      });
    } else if (isFailure(this.keepDiscardResult)) {
      return failure({ key: "general-error" });
    }
  }

  async convertFileToPdf(context: string): Promise<string[]> {
    const res = await this.http.post(`/convert/${context}`);
    return res.data;
  }

  async checkConversionStatus(id: string): Promise<{ id: string; status: string }> {
    const res = await this.http.get(`/convert/${id}/status`);
    return res.data;
  }

  async keepDocumentConversion(
    id: string,
  ): Promise<{ data: Blob; filename: string; newDocumentId?: string }> {
    const res = await this.http.post(`/convert/${id}/keep`, null, {
      responseType: "blob",
    });
    return {
      data: res.data,
      filename: getFilenameFromHeaders(res.headers) ?? "NA",
      newDocumentId: res.headers["x-new-document-id"],
    };
  }

  async discardDocumentConversion(id: string): Promise<void> {
    const res = await this.http.post(`/convert/${id}/discard`, {});
    return res.data;
  }

  async cancelConversion(id: string): Promise<void> {
    const res = await this.http.post(`/convert/${id}/cancel`, null);
    return res.data;
  }
}
